import AspectRatio from '@blocks/AspectRatio'
import Breadcrumps from '@blocks/Breadcrumps'
import Flex from '@blocks/Flex'
import Grid from '@blocks/Grid'
import MainContainer from '@blocks/MainContainer'
import SalonController from '@club/customer/api/SalonController'
import { route } from '@helpers'
import useAuth from '@hooks/useAuth'
import useRequestEffect from '@hooks/useRequestEffect'
import useTitle from '@hooks/useTitle'
import React, { useMemo } from 'react'
import { useParams } from 'react-router'
import styled, { css } from 'styled-components'
import { formatDate } from "@helpers"
import { tablet } from '@styles/mixins'
import SalonMembersTable from './components/MembersTable/SalonMembersTable'
import EmphasizedContent from '@blocks/EmphasizedContent'
import MembersDelivery from './components/MembersDelivery'

export default function SalonPage() {
    const { id } = useParams<{ id: string }>()
    const auth = useAuth<Resources.ClubOwner>()

    const [salon, setSalon] = useRequestEffect(() => SalonController.show(auth.user.club.id, id))
    const name = salon?.name || 'Loading...'

    useTitle(`Member: ${name}`)

    const heading = useMemo(() => <Breadcrumps items={[
        { name: auth.user.club.name, url: route("club:customer") }, 
        { name: 'Salons', url: route("club:owner:salons") }, 
        { name }
    ]} />, [name])

    if (!salon)
        return <MainContainer heading={heading} />
    
    return (
        <MainContainer heading={heading}>
            <Grid>
                <EmphasizedContent>
                    <InfoCard>
                        <SalonBackgroundWrapper>
                            <SalonBackground url={salon.background_url} />
                        </SalonBackgroundWrapper>
                        <InfoWrapper>
                            <Grid gap="15px">
                                <Grid gap="5px">
                                    <h1 className="h3">{salon.name}</h1>
                                    <small>{salon.patronages.map(patronage => `${patronage.organization.name} (${patronage.number})`).join(', ')}</small>
                                </Grid>
                                <Flex gap="15px" flexWrap="wrap">
                                    <div>
                                        <Grid gap="5px">
                                            <small>Type</small>
                                            <b style={{ textTransform: 'capitalize' }}>{salon.type}</b>
                                        </Grid>
                                    </div>
                                    <div>
                                        <Grid gap="5px">
                                            <small>{salon.type === "current" ? "Closing date" : "Results date"}</small>
                                            <b>{salon.type === "current" ? formatDate(salon.closing_date) : formatDate(salon.results_date)}</b>
                                        </Grid>
                                    </div>
                                    <div>
                                        <Grid gap="5px">
                                            <small>Entry Fee</small>
                                            <b>€{salon.fee}</b>
                                        </Grid>
                                    </div>
                                </Flex>
                            </Grid>
                        </InfoWrapper>
                    </InfoCard>
                </EmphasizedContent>
                <MembersDelivery salon={salon} />
                <SalonMembersTable salon={salon} />
            </Grid>
        </MainContainer>
    )
}

const SalonBackgroundWrapper = styled.div`
    width: 100%;
    height: 200px;

    ${tablet(css`
        width: 200px;
        height: 200px;
        flex-shrink: 0;
    `)}
`

const InfoCard = styled.div`
    > div:not(:last-child) {
        margin-bottom: 30px;
    }

    ${tablet(css`
        display: flex;
        align-items: center;

        > div:not(:last-child) {
            margin-bottom: 0;
            margin-right: 30px;
        }
    `)}
`

const InfoWrapper = styled.div`
    width: 100%;
`

const SalonBackground = styled.div<{ url: string }>`
    width: 100%;
    height: 100%;
    background: ${props => `url(${props.url}) center no-repeat`};
    background-size: cover;
    border-radius: 10px;
`